import React from "react";
import { Card, CardBody, CardFooter, CardHeader, Col, Row } from "reactstrap";
import ReactTooltip from "react-tooltip";
import CardLoadingSkeleton from "../../../../components/ui/Loading/CardLoadingSkeleton";
import useLoading from "../Hooks/useLoading";
import moment from "moment";
import useAppliedFilters from "../Hooks/useAppliedFilters";
import { CircleHelp } from "lucide-react";

const ProductivityCardsWrapper = ({
  fieldProductivityData,
  typingProductivityData,
  visitsByStatusData,
}) => {
  const [visitsByStatus, setVisitsByStatus] = React.useState(null);
  const { appliedFilters } = useAppliedFilters();
  const { isInformationsLoading } = useLoading();

  React.useEffect(() => {
    if (!visitsByStatusData || visitsByStatusData.lenght === 0) return;

    const result = getVisitsByStatusObject();

    visitsByStatusData.forEach(({ situation, quantity }) => {
      if (result[situation]) result[situation].quantity = quantity;
    });

    setVisitsByStatus(result);
  }, [visitsByStatusData]);

  function getVisitsByStatusObject() {
    return {
      Aberto: {
        quantity: 0,
      },
      Fechado: {
        quantity: 0,
      },
      "Endereço Não Encontrado": {
        quantity: 0,
      },
      Recusa: {
        quantity: 0,
      },
      Orientação: {
        quantity: 0,
      },
    };
  }

  const styles = {
    row: {
      marginLeft: 0,
      marginRight: 0,
    },
    col: {
      paddingLeft: 0,
      paddingRight: "0.5vw",
      width: "100%",
    },
  };

  if (
    fieldProductivityData.lenght === 0 ||
    typingProductivityData.lenght === 0 ||
    isInformationsLoading ||
    !visitsByStatus
  )
    return (
      <>
        <Row className="mb-2" style={styles.row}>
          <Col style={styles.col}>
            <CardLoadingSkeleton
              topBarWidth="50px"
              middleBarWidth="150px"
              bottomBarWidth="100px"
            />
          </Col>
          <Col style={styles.col}>
            <CardLoadingSkeleton
              topBarWidth="50px"
              middleBarWidth="150px"
              bottomBarWidth="100px"
            />
          </Col>
          <Col style={styles.col}>
            <CardLoadingSkeleton
              topBarWidth="50px"
              middleBarWidth="150px"
              bottomBarWidth="100px"
            />
          </Col>
          <Col style={styles.col}>
            <CardLoadingSkeleton
              topBarWidth="50px"
              middleBarWidth="150px"
              bottomBarWidth="100px"
            />
          </Col>
        </Row>
        <Row className="mb-2" style={styles.row}>
          <Col xl={3} style={styles.col}>
            <CardLoadingSkeleton
              topBarWidth="50px"
              middleBarWidth="180px"
              bottomBarWidth="120px"
            />
          </Col>
          <Col xl={3} style={styles.col}>
            <CardLoadingSkeleton
              topBarWidth="50px"
              middleBarWidth="180px"
              bottomBarWidth="120px"
            />
          </Col>
          <Col xl={3} style={styles.col}>
            <CardLoadingSkeleton
              topBarWidth="50px"
              middleBarWidth="180px"
              bottomBarWidth="120px"
            />
          </Col>
          <Col xl={3} style={styles.col}>
            <CardLoadingSkeleton
              topBarWidth="50px"
              middleBarWidth="180px"
              bottomBarWidth="120px"
            />
          </Col>
        </Row>
      </>
    );

  const appInstalledTraps = fieldProductivityData
    ? fieldProductivityData.totalInstalledTrapsOnField
    : 0;
  const webInstalledTraps = typingProductivityData
    ? typingProductivityData.totalInstalls
    : 0;

  const appReadedTraps = fieldProductivityData
    ? fieldProductivityData.totalMaintenancesTrapsOnField
    : 0;
  const webReadedTraps = typingProductivityData
    ? typingProductivityData.totalMaintenances
    : 0;

  const appUninstalledTraps = fieldProductivityData
    ? fieldProductivityData.totalUninstalledTrapsOnField
    : 0;
  const webUninstalledTraps = typingProductivityData
    ? typingProductivityData.totalUninstalls
    : 0;

  const appUnableToReadTraps = fieldProductivityData
    ? fieldProductivityData.totalUnableToReadTrapsOnField
    : 0;
  const webUnableToReadTraps = typingProductivityData
    ? typingProductivityData.totalUnableToRead
    : 0;

  const periodLegend = `De ${moment(appliedFilters.beginDate).format(
    "DD/MM/YYYY"
  )} à ${moment(appliedFilters.endDate).format("DD/MM/YYYY")}`;

  return (
    <>
      <Row className="mb-4" style={styles.row}>
        <Col style={styles.col}>
          <SinanCard>
            <SinanCardHeader
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>Instalações de armadilhas</span>
              <CircleHelp data-tip data-for="totalInstalledTraps" size={17} />
              <ReactTooltip effect="solid" type="info" id="totalInstalledTraps">
                App: {appInstalledTraps} / Web: {webInstalledTraps}
              </ReactTooltip>
            </SinanCardHeader>
            <SinanCardContent>
              <div>
                <span style={{ fontWeight: 600, fontSize: "1.5rem" }}>
                  {appInstalledTraps + webInstalledTraps}
                </span>
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: ".5rem" }}
              ></div>
            </SinanCardContent>
            <SinanCardFooter>{periodLegend}</SinanCardFooter>
          </SinanCard>
        </Col>
        <Col style={styles.col}>
          <SinanCard>
            <SinanCardHeader
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>Manutenções de armadilhas</span>
              <CircleHelp data-tip data-for="totalReadedTraps" size={17} />
              <ReactTooltip effect="solid" type="info" id="totalReadedTraps">
                App: {appReadedTraps} / Web: {webReadedTraps}
              </ReactTooltip>
            </SinanCardHeader>
            <SinanCardContent>
              <div>
                <span style={{ fontWeight: 600, fontSize: "1.5rem" }}>
                  {appReadedTraps + webReadedTraps}
                </span>
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: ".5rem" }}
              ></div>
            </SinanCardContent>
            <SinanCardFooter>{periodLegend}</SinanCardFooter>
          </SinanCard>
        </Col>
        <Col style={styles.col}>
          <SinanCard>
            <SinanCardHeader
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>Desinstalações de armadilhas</span>
              <CircleHelp data-tip data-for="totalUninstalledTraps" size={17} />
              <ReactTooltip
                effect="solid"
                type="info"
                id="totalUninstalledTraps"
              >
                App: {appUninstalledTraps} / Web: {webUninstalledTraps}
              </ReactTooltip>
            </SinanCardHeader>
            <SinanCardContent>
              <div>
                <span style={{ fontWeight: 600, fontSize: "1.5rem" }}>
                  {appUninstalledTraps + webUninstalledTraps}
                </span>
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: ".5rem" }}
              ></div>
            </SinanCardContent>
            <SinanCardFooter>{periodLegend}</SinanCardFooter>
          </SinanCard>
        </Col>
        <Col style={styles.col}>
          <SinanCard>
            <SinanCardHeader
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>Impossibilidade de leitura de armadilhas</span>
              <CircleHelp
                data-tip
                data-for="totalUnableToReadTraps"
                size={17}
              />
              <ReactTooltip
                effect="solid"
                type="info"
                id="totalUnableToReadTraps"
              >
                App: {appUnableToReadTraps} / Web: {webUnableToReadTraps}
              </ReactTooltip>
            </SinanCardHeader>
            <SinanCardContent>
              <div>
                <span style={{ fontWeight: 600, fontSize: "1.5rem" }}>
                  {appUnableToReadTraps + webUnableToReadTraps}
                </span>
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: ".5rem" }}
              ></div>
            </SinanCardContent>
            <SinanCardFooter>{periodLegend}</SinanCardFooter>
          </SinanCard>
        </Col>
      </Row>
      <Row className="mb-4" style={styles.row}>
        <Col style={styles.col}>
          <SinanCard>
            <SinanCardHeader>Visitas em "Aberto"</SinanCardHeader>
            <SinanCardContent>
              <div>
                <span style={{ fontWeight: 600, fontSize: "1.5rem" }}>
                  {visitsByStatus && visitsByStatus.Aberto
                    ? visitsByStatus.Aberto.quantity
                    : 0}
                </span>
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: ".5rem" }}
              ></div>
            </SinanCardContent>
            <SinanCardFooter>{periodLegend}</SinanCardFooter>
          </SinanCard>
        </Col>
        <Col style={styles.col}>
          <SinanCard>
            <SinanCardHeader>Visitas em "Fechado"</SinanCardHeader>
            <SinanCardContent>
              <div>
                <span style={{ fontWeight: 600, fontSize: "1.5rem" }}>
                  {visitsByStatus && visitsByStatus.Fechado
                    ? visitsByStatus.Fechado.quantity
                    : 0}
                </span>
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: ".5rem" }}
              ></div>
            </SinanCardContent>
            <SinanCardFooter>{periodLegend}</SinanCardFooter>
          </SinanCard>
        </Col>
        <Col style={styles.col}>
          <SinanCard>
            <SinanCardHeader>Visitas em "Recusa"</SinanCardHeader>
            <SinanCardContent>
              <div>
                <span style={{ fontWeight: 600, fontSize: "1.5rem" }}>
                  {visitsByStatus && visitsByStatus.Recusa
                    ? visitsByStatus.Recusa.quantity
                    : 0}
                </span>
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: ".5rem" }}
              ></div>
            </SinanCardContent>
            <SinanCardFooter>{periodLegend}</SinanCardFooter>
          </SinanCard>
        </Col>
        <Col style={styles.col}>
          <SinanCard>
            <SinanCardHeader>
              Visitas em "Endereço não encontrado"
            </SinanCardHeader>
            <SinanCardContent>
              <div>
                <span style={{ fontWeight: 600, fontSize: "1.5rem" }}>
                  {visitsByStatus && visitsByStatus["Endereço Não Encontrado"]
                    ? visitsByStatus["Endereço Não Encontrado"].quantity
                    : 0}
                </span>
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: ".5rem" }}
              ></div>
            </SinanCardContent>
            <SinanCardFooter>{periodLegend}</SinanCardFooter>
          </SinanCard>
        </Col>
      </Row>
    </>
  );
};

const SinanCard = ({ children, style, props }) => {
  return (
    <Card style={{ ...style, borderColor: "#dfdfdf" }} {...props}>
      {children}
    </Card>
  );
};

const SinanCardHeader = ({ children, style, props }) => {
  return (
    <CardHeader
      style={{
        ...style,
        borderBottom: "0px",
        fontWeight: 600,
        paddingBottom: "0px",
      }}
      {...props}
    >
      {children}
    </CardHeader>
  );
};

const SinanCardContent = ({ children, style, props }) => {
  return (
    <CardBody
      style={{
        ...style,
        paddingTop: "0px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
      {...props}
    >
      {children}
    </CardBody>
  );
};

const SinanCardFooter = ({ children, style, props }) => {
  return (
    <CardFooter
      style={{ style, backgroundColor: "#f7f7f7", borderTop: "0px" }}
      {...props}
    >
      {children}
    </CardFooter>
  );
};

export default ProductivityCardsWrapper;
